import AppDescription from '@nxsys/components/AppDescription';
import AppLoader from '@nxsys/components/AppLoader';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { paymentSettingActions } from '@nxsys/store/slices/paymentSetting/reducer/paymentSettingSlice';
import {
  PaymentRate,
  UpdatePaymentRateParams,
} from '@nxsys/store/slices/paymentSetting/types';
import { Icons } from '@nxsys/types/icons';
import {
  formatPaymentType,
  formatPaymentUnit,
  getVATCodeOption,
  makeCode,
} from '@nxsys/utils';
import { Descriptions, Tag } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PaymentRateForm, {
  PaymentRateFormRef,
  PaymentRateFormValues,
} from '../components/PaymentRateForm';
import { defaultPaymentRateTypeOptions } from '@nxsys/constants/AppConstants';

type ViewPaymentRateProps = {
  isOpen: boolean;
  toggle: () => void;
  paymentRateId: string | undefined;
  refetchData: () => void;
};

const ViewPaymentRateModal: React.FC<ViewPaymentRateProps> = ({
  isOpen,
  toggle,
  paymentRateId,
  refetchData,
}: ViewPaymentRateProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const dispatch = useDispatch<AppDispatch>();

  const { vatCodeList } = useSelector(
    (state: AppState) => state.paymentSettingStore,
  );
  const { loading: vatCodeListLoading } = vatCodeList;

  const formRef = useRef<PaymentRateFormRef>(null);

  const defaultCode = useMemo(() => makeCode('PR', 3), []);

  const [data, setData] = useState<PaymentRate | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(
      paymentSettingActions.getVATCodeList({
        page: 0,
        size: 20,
      }),
    );
  }, []);

  const getDetail = async (id: string) => {
    setIsLoadingData(true);
    const res = await dispatch(
      paymentSettingActions.getPaymentRateById({ id }),
    ).unwrap();
    setData(res.data);
    setIsLoadingData(false);
  };

  useEffect(() => {
    if (paymentRateId && isOpen) getDetail(paymentRateId);
  }, [isOpen, paymentRateId]);

  const handleSubmit = (values: PaymentRateFormValues) => {
    if (!data?.id) return;

    setIsSubmitting(true);
    let params: UpdatePaymentRateParams['payload'];
    // NX-6038: the API rejects any other field changing on a default rate,
    // and the hardcoded flags below would not match every stored value
    if (data.isDefaultRate) {
      params = { description: values.description ?? '' };
    }
    // NX-5228: country decides the payload shape, so it is tested first.
    // Checking `!isBureau` first sent an umbrella-Ireland edit the UK-shaped
    // flags and dropped PRSI/USC/ASC/LPT and includeInPayeCalculation.
    else if (isIreland) {
      params = {
        code: values.code ?? defaultCode,
        description: values.description ?? '',
        status: values?.status ?? '',
        displayOnPayslip: Boolean(values.displayOnPayslip),
        type: values?.type?.value ?? null,
        units: values?.units ?? '',
        hoursPerUnit: values?.hoursPerUnit ?? '',
        defaultVATCode: values.defaultVATCode ?? '',
        taxCanBeDeducted: Boolean(values.taxCanBeDeducted),
        employeePRSICanBeDeducted: Boolean(values.employeePRSICanBeDeducted),
        employerPRSICanBeDeducted: Boolean(values.employerPRSICanBeDeducted),
        uscCanBeDeducted: Boolean(values.uscCanBeDeducted),
        ascCanBeDeducted: Boolean(values.ascCanBeDeducted),
        pensionSchemaDeductionCanBeDeducted: Boolean(
          values.pensionSchemaDeductionCanBeDeducted,
        ),
        lptCanBeDeducted: Boolean(values.lptCanBeDeducted),
        includeAccrueHolidayPay: Boolean(values.includeAccrueHolidayPay),
        excludeFromNmwCalculation: Boolean(values.excludeFromNmwCalculation),
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
        includeInPayeCalculation: true,
      };
    } else if (!isBureau) {
      params = {
        code: values.code ?? defaultCode,
        defaultVATCode: values.defaultVATCode ?? '',
        description: values.description ?? '',
        displayOnPayslip: Boolean(values.displayOnPayslip),
        hoursPerUnit: values?.hoursPerUnit ?? '',
        includeAccrueHolidayPay: Boolean(values.includeAccrueHolidayPay),
        excludeFromNmwCalculation: Boolean(values.excludeFromNmwCalculation),
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
        includeNICalculation: Boolean(values.includeNICalculation),
        includePensionCalculation: Boolean(values.includePensionCalculation),
        includeTaxCalculation: Boolean(values.includeTaxCalculation),
        raiseInvoice: Boolean(values.raiseInvoice),
        status: values?.status ?? '',
        type: values?.type?.value ?? null,
        units: values?.units ?? '',
      };
    } else {
      params = {
        code: values.code ?? defaultCode,
        defaultVATCode: values.defaultVATCode ?? '',
        description: values.description ?? '',
        displayOnPayslip: Boolean(values.displayOnPayslip),
        hoursPerUnit: values?.hoursPerUnit ?? '',
        includeAccrueHolidayPay: Boolean(values.includeAccrueHolidayPay),
        excludeFromNmwCalculation: Boolean(values.excludeFromNmwCalculation),
        includeNICalculation: Boolean(values.includeNICalculation),
        includePensionCalculation: Boolean(values.includePensionCalculation),
        includeTaxCalculation: Boolean(values.includeTaxCalculation),
        status: values?.status ?? '',
        type: values?.type?.value ?? null,
        units: values?.units ?? '',
        includeInPayeCalculation: true,
      };
    }
    dispatch(
      paymentSettingActions.updatePaymentRateById({
        id: data.id,
        payload: params,
      }),
    )
      .then((res) => {
        refetchData();
        setData((res.payload as any)?.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const descriptionItems = [
    {
      label: messages['paymentSetting.paymentRate.code'],
      value: data?.code ?? '',
    },
    {
      label: messages['paymentSetting.payment.description'],
      value: data?.description ?? '',
    },
    {
      label: messages['common.type'],
      value: formatPaymentType(data?.type ?? ''),
    },
    {
      label: messages['common.units'],
      value: formatPaymentUnit(data?.units ?? ''),
    },
    {
      label: `${messages['common.hours']} / ${messages['common.unit']}`,
      value: data?.hoursPerUnit ?? '',
    },
    {
      label: messages['paymentSetting.paymentRate.defaultVATCode'],
      value: data?.defaultVATCode
        ? (getVATCodeOption(data?.defaultVATCode)?.label ?? '')
        : '',
    },
    {
      label: messages['common.status'],
      value: (
        <Tag>
          {data?.status ? messages['common.yes'] : messages['common.no']}
        </Tag>
      ),
    },
    {
      label: messages['paymentSetting.payment.displayOnPayslip'],
      value: (
        <Tag>
          {data?.displayOnPayslip
            ? messages['common.yes']
            : messages['common.no']}
        </Tag>
      ),
    },
    ...(!isIreland
      ? [
          ...(!isBureau
            ? [
                {
                  label: messages['paymentSetting.payment.beforeCisTax'],
                  value: (
                    <Tag>
                      {data?.includeInCisCalculation
                        ? messages['common.yes']
                        : messages['common.no']}
                    </Tag>
                  ),
                },
              ]
            : []),
          {
            label: messages['paymentSetting.payment.beforeTax'],
            value: (
              <Tag>
                {data?.includeTaxCalculation
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeNI'],
            value: (
              <Tag>
                {data?.includeNICalculation
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforePension'],
            value: (
              <Tag>
                {data?.includePensionCalculation
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.paymentRate.accrueHolidayPay'],
            value: (
              <Tag>
                {data?.includeAccrueHolidayPay
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.paymentRate.excludeFromNmwCalculation'],
            value: (
              <Tag>
                {data?.excludeFromNmwCalculation
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          ...(!isBureau
            ? [
                {
                  label: messages['paymentSetting.paymentRate.raiseInvoice'],
                  value: (
                    <Tag>
                      {data?.raiseInvoice
                        ? messages['common.yes']
                        : messages['common.no']}
                    </Tag>
                  ),
                },
              ]
            : []),
        ]
      : [
          {
            label: messages['paymentSetting.payment.beforeTax'],
            value: (
              <Tag>
                {data?.taxCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeEmployeePRSI'],
            value: (
              <Tag>
                {data?.employeePRSICanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeEmployerPRSI'],
            value: (
              <Tag>
                {data?.employerPRSICanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeUSC'],
            value: (
              <Tag>
                {data?.uscCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeASC'],
            value: (
              <Tag>
                {data?.ascCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label:
              messages['paymentSetting.payment.beforePensionSchemeDeductions'],
            value: (
              <Tag>
                {data?.pensionSchemaDeductionCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeLPT'],
            value: (
              <Tag>
                {data?.lptCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.paymentRate.accrueHolidayPay'],
            value: (
              <Tag>
                {data?.includeAccrueHolidayPay
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.paymentRate.excludeFromNmwCalculation'],
            value: (
              <Tag>
                {data?.excludeFromNmwCalculation
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeRCT'],
            value: (
              <Tag>
                {data?.includeInCisCalculation
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
        ]),
  ];

  return (
    <AppModalContainer
      title={messages['paymentSetting.paymentRate.paymentRateDetail']}
      isOpen={isOpen}
      toggle={toggle}
      actions={
        isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.save'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                loading: isSubmitting,
                disabled: isSubmitting,
              },
            ]
          : [
              {
                name: messages['common.edit'],
                buttonType: 'primary',
                leftIcon: (
                  <Icons.IconEdit color={defaultTheme.theme.palette.white} />
                ),
                onClick: () => setIsEditing(true),
              },
            ]
      }
    >
      {(isLoadingData || vatCodeListLoading || isSubmitting) && (
        <AppLoader />
      )}
      {data && (
        <>
          {!isEditing ? (
            <AppDescription
              column={1}
              labelStyle={{ width: 216 }}
              bordered={false}
              colon={false}
              size="middle"
            >
              {descriptionItems?.map((item, _index) => (
                <Descriptions.Item key={_index} label={item.label} span={1}>
                  {item.value ?? '-'}
                </Descriptions.Item>
              ))}
            </AppDescription>
          ) : (
            <PaymentRateForm
              formRef={formRef}
              onSubmit={handleSubmit}
              onlyDescriptionEditable={data.isDefaultRate}
              defaultValues={{
                code: data?.code,
                description: data?.description,
                type:
                  defaultPaymentRateTypeOptions.find(
                    (item) => item.value === data?.type,
                  ) ?? null,
                units: data?.units,
                hoursPerUnit: data?.hoursPerUnit ?? '',
                defaultVATCode: data?.defaultVATCode?.id,
                status: data?.status,
                displayOnPayslip: data?.displayOnPayslip,
                ...(!isBureau || isIreland
                  ? {
                      includeInCisCalculation: data?.includeInCisCalculation,
                    }
                  : {}),
                ...(!isIreland
                  ? {
                      includeTaxCalculation: data?.includeTaxCalculation,
                      includeNICalculation: data?.includeNICalculation,
                      includePensionCalculation:
                        data?.includePensionCalculation,
                      includeAccrueHolidayPay: data?.includeAccrueHolidayPay,
                      excludeFromNmwCalculation:
                        data?.excludeFromNmwCalculation,
                      raiseInvoice: data?.raiseInvoice,
                    }
                  : {
                      taxCanBeDeducted: data?.taxCanBeDeducted,
                      employeePRSICanBeDeducted:
                        data?.employeePRSICanBeDeducted,
                      employerPRSICanBeDeducted:
                        data?.employerPRSICanBeDeducted,
                      uscCanBeDeducted: data?.uscCanBeDeducted,
                      ascCanBeDeducted: data?.ascCanBeDeducted,
                      pensionSchemaDeductionCanBeDeducted:
                        data?.pensionSchemaDeductionCanBeDeducted,
                      lptCanBeDeducted: data?.lptCanBeDeducted,
                      includeAccrueHolidayPay: data?.includeAccrueHolidayPay,
                      excludeFromNmwCalculation:
                        data?.excludeFromNmwCalculation,
                    }),
              }}
            />
          )}
        </>
      )}
    </AppModalContainer>
  );
};

export default ViewPaymentRateModal;
